/**
 * Constantes Generales - Migrado de ConstantsEJB.java
 * 
 * Centraliza constantes del legacy GWT para evitar código "quemado".
 * 
 * @see 580-Girop-ml/PrestacionServiciosCGREJB/ejbModule/src/com/sura/prestacionservicioscgr/application/ConstantsEJB.java
 * @author Ingeneo - Migración GWT a Angular
 */

import { environment } from 'src/environments/environment';

// ============================================================================
// VERSIÓN DE LA APLICACIÓN
// ============================================================================
export const APP_VERSION = '2.0';

// ============================================================================
// FLAGS BOOLEANOS (S/N) - Legacy: STRING_ACTIVE, STRING_INACTIVE, YES_VALUE, NO_VALUE
// ============================================================================

/** Valor para registros activos */
export const STRING_ACTIVE = 'S';

/** Valor para registros inactivos */
export const STRING_INACTIVE = 'N';

/** Valor afirmativo para campos SN_* */
export const YES_VALUE = 'S';

/** Valor negativo para campos SN_* */
export const NO_VALUE = 'N';

// ============================================================================
// VALORES POR DEFECTO - Legacy: DEFAULT_LIST_VALUE, DEFAULT_COMPANY
// ============================================================================

/** Valor por defecto para listas desplegables (seleccione opción) */
export const DEFAULT_LIST_VALUE = '-1';

/** Compañía por defecto (Sura) */
export const DEFAULT_COMPANY = '1';

// ============================================================================
// PAGINACIÓN - Legacy: ConstantsEJB.DEFAULT_ROWS_PER_PAGE = 10
// ============================================================================

/**
 * Número de filas por página por defecto.
 * Legacy: ConstantsEJB.DEFAULT_ROWS_PER_PAGE = 10
 */
export const DEFAULT_ROWS_PER_PAGE = 10;

/**
 * Opciones de tamaño de página disponibles en dropdowns.
 * Configuración estándar para todos los listados paginados.
 */
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * Tamaño máximo de página permitido.
 * Evita consultas excesivamente grandes.
 */
export const MAX_PAGE_SIZE = 100;

/**
 * Tamaño mínimo de página permitido.
 */
export const MIN_PAGE_SIZE = 5;

/**
 * Rango máximo de fechas de aprobación permitido en meses.
 * Legacy: FilterProgramarServiciosViewImpl.RANGO_DEFAULT_FEAPR = 6
 */
export const RANGO_DEFAULT_FEAPR = 6;

// ============================================================================
// SSO / SESIÓN - Claves de sessionStorage para autenticación SEUS
// ============================================================================

/**
 * Clave para almacenar el DNI (username) del usuario autenticado via SSO (SEUS).
 * Persistido por IdentityServiceImpl al recibir respuesta de /security/api/user/current.
 * Es la fuente primaria de identidad antes de que se cargue la sesión GIROP completa.
   
										   
								
									
   
																				 
																		 
																		 
  
																			   
																
 */
export const SESSION_KEY_IDENTITY_DNI = 'GIROP_IDENTITY_DNI';

					 
						  
								   
/**
 * Clave para almacenar datos de usuario en sessionStorage.
 * Usada por UsuarioStateService y adaptadores HTTP.
 */
export const SESSION_KEY_USUARIO = 'GIROP_USUARIO_SESION';

// ============================================================================
// TIPOS DE IDENTIFICACIÓN - Legacy: Constants.LIST_VALUE_TYPE_ID_*
// ============================================================================

export const TIPO_IDENTIFICACION = {
  /** Cédula de Ciudadanía */
  CEDULA: 'C',
  /** NIT - Número de Identificación Tributaria */
  NIT: 'N',
  /** Cédula de Extranjería */
  EXTRANJERIA: 'E',
  /** Pasaporte */
  PASAPORTE: 'P',
  /** Tarjeta de Identidad */
  TARJETA_IDENTIDAD: 'T',
  /** Registro Civil */
  REGISTRO_CIVIL: 'I',
  /** Carné Diplomático */
  CARNE_DIPLOMATICO: 'D',
  /** Permiso Protección Temporal */
  PERMISO_PROTECCION: 'X'
} as const;

/**
 * Mapa de códigos de tipo de identificación a descripción.
 * Usado para mostrar nombres legibles en UI.
 * Legacy: Cargado dinámicamente desde BD, pero valores son estables.
 */
export const TIPO_IDENTIFICACION_DESCRIPCION: Record<string, string> = {
  'C': 'CEDULA DE CIUDADANIA',
  'N': 'NIT',
  'E': 'CEDULA DE EXTRANJERIA',
  'P': 'PASAPORTE',
  'T': 'TARJETA DE IDENTIDAD',
  'I': 'REGISTRO CIVIL',
  'D': 'CARNE DIPLOMATICO',
  'X': 'PERMISO PROTECCION TEMPORAL'
};

const DEFAULT_TIPO_IDENTIFICACION_DESCRIPCION = 'CEDULA DE CIUDADANIA';

/**
 * Convierte código de tipo de identificación a descripción legible.
 * @param codigo Código del tipo (C, N, E, P, T, I, D, X)
 * @returns Descripción legible o 'CEDULA DE CIUDADANIA' por defecto
 */
export function getTipoIdentificacionDescripcion(codigo: string | undefined | null): string {
  if (!codigo) {
    return DEFAULT_TIPO_IDENTIFICACION_DESCRIPCION;
  }
  return TIPO_IDENTIFICACION_DESCRIPCION[codigo.toUpperCase()] || DEFAULT_TIPO_IDENTIFICACION_DESCRIPCION;
}

// ============================================================================
// TIPOS DE SEXO - Legacy: WidgetsNamesConstants.properties (male/female)
// ============================================================================

/**
 * Tipos de sexo para dropdowns.
 * Legacy: WidgetsNamesConstants.properties - male=Masculino, female=Femenino
 * Son valores fijos del sistema, no requieren carga de BD.
 */
export const TIPOS_SEXO = [
  { codigo: 'M', descripcion: 'Masculino' },
  { codigo: 'F', descripcion: 'Femenino' }
];

/**
 * Mapa de códigos de sexo a descripción.
 * Usado por getSexoDisplay() y otros helpers.
 */
export const SEXO_DISPLAY: Record<string, string> = {
  'M': 'Masculino',
  'F': 'Femenino'
};

// ============================================================================
// ESTADOS DE CITAS - Legacy: ConstantsEJB.TpscEstados
// ============================================================================

/**
 * Estados de Cita - Tabla TPSC_ESTADOS
 * @see ConstantsEJB.TpscEstados
 */
export const ESTADO_CITA = {
  /** Cita programada - TpscEstados.CITA_PROGRAMADA */
  PROGRAMADA: '0015',
  /** Cita ejecutada - ESTADO_EJECUTADO_CITA_DISTRI */
  EJECUTADA: '0016',
  /** Cita cancelada - TpscEstados.CITA_CANCELADA */
  CANCELADA: '0017',
  /** Cita reprogramada */
  REPROGRAMADA: '0018',
  /** Cita reservada - TpscEstados.CITA_RESERVADA */
  RESERVADA: '0019',
  
  // Códigos alternativos usados en algunos contextos
  PRG: 'PRG',
  EJE: 'EJE',
  CAN: 'CAN',
  REP: 'REP',
  PARCIAL: 'PARCIAL',
  PEJ: 'PEJ'
} as const;

// ============================================================================
// ESTADOS DE TAREAS - Legacy: ConstantsEJB.TpscEstados
// ============================================================================

export const ESTADO_TAREA = {
  /** Pendiente de gestión */
  PENDIENTE_GESTION: '0008',
  /** Programada */
  PROGRAMADA: '0003',
  /** Parcialmente programada */
  PARCIALMENTE_PROGRAMADA: '0004',
  /** Ejecutada */
  EJECUTADA: '0010',
  /** Programada parcialmente cumplido */
  PROGRAMADA_PARCIALMENTE_CUMPLIDO: '0030',
  /** Parcialmente programada y parcialmente cumplida */
  PARCIALMENTE_PROGRAMADA_Y_CUMPLIDA: '0031',
  /** Parcialmente programada y cumplido lo programado */
  PARCIALMENTE_PROGRAMADA_CUMPLIDO_LO_PROG: '0032',
  /** Sin programación */
  SIN_PROGRAMACION: '0033',
  /** Cancelada */
  CANCELADA: '0050'
} as const;

// ============================================================================
// ESTADOS DE INFORMES - Legacy: ConstantsEJB.TpscEstados
// ============================================================================

export const ESTADO_INFORME = {
  /** Rechazado */
  RECHAZADO: 'RE',
  /** Aprobado */
  APROBADO: 'AP',
  /** Enviado */
  ENVIADO: 'EN',
  /** Parcial */
  PARCIAL: 'PA'
} as const;

// ============================================================================
// ESTADOS DE RIESGOS POTENCIALES - Legacy: ConstantsEJB.TpscEstados
// ============================================================================

export const ESTADO_RIESGO = {
  /** Reportado */
  REPORTADO: 'RPRP',
  /** Escalado Nivel 1 */
  ESCALADO_N1: 'RPE1',
  /** Escalado Nivel 2 */
  ESCALADO_N2: 'RPE2',
  /** Escalado Nivel 3 (Cerrado Condicionado) */
  ESCALADO_N3: 'RPE3',
  /** Cerrado */
  CERRADO: 'RPCR',
  /** Cerrado No Aplica */
  CERRADO_NO_APLICA: 'RPCA',
  /** Cerrado por desafiliación */
  CERRADO_DESAFILIACION: 'RPCD',
  /** Reabierto */
  REABIERTO: 'RPRA'
} as const;

// ============================================================================
// TIPOS DE CITAS - Legacy: ConstantsEJB.TpscTiposDeCitas
// ============================================================================

export const TIPO_CITA = {
  /** Campo */
  CAMPO: 17,
  /** Informe */
  INFORME: 18,
  /** Campo Complementario */
  CAMPO_COMPLEMENTARIO: 42,
  /** Informe Complementario */
  INFORME_COMPLEMENTARIO: 43
} as const;

/**
 * Tipo de manejo de la unidad de medida (TPSC_TIPOS_UNDS_MEDIDA.DSTIPOMANEJO).
 * @see Constants.MEASURE_UNITS_TIME (legacy) = 'T'
 */
export const MANEJO_TIPO_TIEMPO = 'T';
export const MANEJO_TIPO_OTRO = 'O';

// ============================================================================
// CONTACTO DEL CUMPLIMIENTO - largos máximos
// Legacy: CreateOrUpdateCumplimientoViewImpl.ui.xml (tbNombreContacto maxLength=50,
// tbCorreoContacto maxLength=100). Backend: ActualizarContactoRequest.MAX_NOMBRE / MAX_EMAIL.
// ============================================================================

/** Largo máximo del nombre del contacto principal. */
export const MAX_NOMBRE_CONTACTO = 50;
/** Largo máximo del correo del contacto principal. */
export const MAX_CORREO_CONTACTO = 100;

// ============================================================================
// OBSERVACIONES DE LA CITA - largo máximo
// Legacy: CrearCitaAdministrativaViewImpl.ui.xml (staNotes maxLength=1000).
// Backend: TextLimits.MAX_OBSERVACIONES_CITA (agenda-ms).
// ============================================================================

/** Largo máximo de las observaciones de la cita. */
export const MAX_OBSERVACIONES_CITA = 1000;

// ============================================================================
// OBSERVACIONES DE LA ORDEN (Cambios en Orientaciones: rechazar / bajar saldo) - largo máximo
// BD: T094_ORDEN_TAREAS.DSMOTIVO_CANCELACION VARCHAR2(200 BYTE), donde SP_PSC_CANCELAR_ORDEN guarda
// el texto (ORA-12899 si se excede). Manda sobre TPSC_ORDEN_SERVICIO_DETAL.DSOBSERVACIONES (400).
// La BD es WE8ISO8859P15 (1 byte por carácter, tildes y Ñ incluidas): 200 bytes = 200 caracteres.
// El legacy permitía 500 caracteres, pero lo que pasara de 200 fallaba igual en el SP.
// Backend: CambioOrientacionController.MAX_OBSERVACIONES_ORDEN (= MotivoCancelacion.MAX_LONGITUD).
// ============================================================================

/** Largo máximo de las observaciones al rechazar o bajar saldo de una orden. */
export const MAX_OBSERVACIONES_ORDEN = 200;

// ============================================================================
// RECOMENDACIONES DEL CUMPLIMIENTO - largo máximo
// Backend: RegistrarCumplimientoRequest.recomendaciones (@SafeText max = 4000).
// ============================================================================

/** Largo máximo de las recomendaciones del cumplimiento. */
export const MAX_RECOMENDACIONES_CUMPLIMIENTO = 4000;

// ============================================================================
// OBSERVACIÓN DE LA NOTIFICACIÓN (correo al contacto en Sin OC de línea de atención) - largo máximo
// Legacy: CreateOrUpdateCumplimientoViewImpl.taObsNotifica, setMaxLength con el parámetro
// MAX_LENGTH_OBSERVAC de la BD (valor por confirmar, pendiente P8a del plan de correo).
// Backend: RegistrarCumplimientoRequest.MAX_OBSERVACION_NOTIFICACION (@Size + @SafeText FREE_TEXT).
// ============================================================================

/** Largo máximo de la observación que va en el correo al contacto. */
export const MAX_OBSERVACION_NOTIFICACION = 4000;

// Textos libres con contador de caracteres (ContadorCaracteresDirective).
// Cada valor es el límite que el campo ya aplicaba (maxlength); el backend lo
// valida con @SafeText / Bean Validation y sigue siendo la fuente definitiva.

/** Observaciones de la justificación tardía (cita y cumplimiento). */
export const MAX_OBSERVACIONES_JUSTIFICACION_TARDIA = 500;
/** Cada campo de texto del acta de cumplimiento. */
export const MAX_TEXTO_ACTA = 3500;
/** Observaciones al enviar el informe. */
export const MAX_OBSERVACIONES_ENVIO_INFORME = 200;
/** Observaciones de la justificación del cumplimiento. */
export const MAX_JUSTIFICACION_CUMPLIMIENTO = 4000;
/** Observaciones del cumplimiento. */
export const MAX_OBSERVACIONES_CUMPLIMIENTO = 4000;
/** Justificación de la anulación en Devolución de cumplimientos. */
export const MAX_JUSTIFICACION_ANULACION = 500;
/** Observaciones de la gestión de tarea. */
export const MAX_OBSERVACIONES_GESTION_TAREA = 2000;
/** Observaciones de la gestión en Sin Orientación de Servicios. */
export const MAX_OBSERVACIONES_GESTION_SIN_OC = 500;

// ============================================================================
// EXPEDIENTE (Sin Orientación de Servicios)
// Legacy: CumplimientoSinOcViewImpl (valorExpediente.length() > 38). SP: inunmexpediente
// NUMBER(38). Backend: CrearTareaRequest.nmexpediente (@Digits integer = 38).
// Viaja como texto porque un number de JavaScript solo conserva ~15 dígitos exactos.
// ============================================================================

/** Cantidad máxima de dígitos del número de expediente. */
export const MAX_DIGITOS_EXPEDIENTE = 38;

// ============================================================================
// VALOR TRANSPORTE ADICIONAL (Registro de cumplimientos)
// Legacy: CreateOrUpdateCumplimientoViewImpl.tbValorTransporteAdicional (maxLength=6,
// solo dígitos, tope GeneralParameters.MAX_TTE_ADIC_CMPL). El tope de negocio llega en
// GET /orientacionesProgramadas/configuracion (maxTransporteAdicional). Backend:
// RegistrarCumplimientoRequest (@Digits integer = 6) + RegistrarCumplimientoUseCase.
// ============================================================================

/** Valor máximo estructural (6 dígitos) cuando no hay tope parametrizado. */
export const MAX_VALOR_TRANSPORTE_ADICIONAL = 999999;

// ============================================================================
// CANTIDADES DECIMALES (horas, duración, cantidad programada/ejecutada)
// Las pantallas usan coma como separador decimal (locale es-CO). Los campos
// numéricos con decimales aceptan hasta 2 posiciones decimales.
// ============================================================================

/** Locale de los campos numéricos: coma decimal, punto de miles. */
export const LOCALE_NUMERICO = 'es-CO';

/** Máximo de posiciones decimales en cantidades (horas, duración, cantidad). */
export const MAX_DECIMALES_CANTIDAD = 2;

// ============================================================================
// DNI DEL BENEFICIARIO (Sin Orientación de Servicios — Ingresar Gestión)
// Legacy: CumplimientoSinOcViewImpl toma M094_CUERPOLIZA.DNI (tipo + número) y
// GestionLlamadaFacade lo envía a SP_PSC_CREAR_BENEFICI_PERSONA (ivadni).
// Backend: GestionLlamadaRequest.MAX_DNI_BENEFICIARIO.
// ============================================================================

/** Longitud máxima del DNI completo del beneficiario. */
export const MAX_DNI_BENEFICIARIO = 30;

/**
 * Longitud máxima del nombre comercial del beneficiario (M094_CUERPOLIZA.DSCOMERCIAL).
 * Backend: BeneficiarioLimits.MAX_NOMBRE (el legacy no define un límite explícito).
 */
export const MAX_NOMBRE_BENEFICIARIO = 200;

// ============================================================================
// CLASIFICACIONES DE HOMOLOGACIÓN (tipo de orden de servicio)
// Legacy: TPSC_CLAS_HOMOLOG_CLIENT.CDCLASIFICACION
// ============================================================================

/**
 * Códigos de clasificación del servicio homologado, que es lo que el negocio
 * llama "tipo de orden de servicio" (Capacitación / Asesoría / …).
 *
 * Legacy: `CreateOrUpdateCumplimientoViewImpl.setTpscOrdenservicioDetalDto()`
 * lee `HO.CDCLASIFICACION` y ejecuta `clasificacionCapacitacion("3".equals(cap))`,
 * que restringe el combo de tipo de cita a `NMCONSECUTIVO = 17` (Campo) cuando
 * la clasificación es Capacitación. Para el resto (p. ej. Asesoría) se muestran
 * todos los tipos de cita de uso SERVICIO (Campo e Informe).
 */
export const CLASIFICACION_HOMOLOGACION = {
  /** Capacitación */
  CAPACITACION: '3'
} as const;

/**
 * Prefijo de `DSCLASIFICACION` para Capacitación.
 * Legacy: `Constants.ACTA_CAPACITACION = "CAPACITACI"`.
 * Se usa como señal secundaria cuando solo se dispone de la descripción.
 */
export const PREFIJO_DSCLASIFICACION_CAPACITACION = 'CAPACITACI';

// ============================================================================
// TIPOS DE ASESORES - Legacy: ConstantsEJB.TpscTiposAsesoresReal
// ============================================================================

export const TIPO_ASESOR = {
  /** Asesor de Riesgos */
  RIESGOS: 'R',
  /** Asesor de Prevención */
  PREVENCION: 'P',
  /** Asesor Integral */
  INTEGRAL: 'I',
  /** Higiene */
  HIGIENE: 'H',
  /** Línea de Atención */
  LINEA_ATENCION: 'L'
} as const;

// ============================================================================
// TIPOS DE EMPLEADOS - Legacy: ConstantsEJB.TpscTiposEmpleados
// ============================================================================

export const TIPO_EMPLEADO = {
  /** Asesor */
  ASESOR: 'A'
} as const;

// ============================================================================
// TIPOS DE PLAN DE ATENCIÓN - Legacy: ConstantsEJB.T094TiposPlan
// ============================================================================

export const TIPO_PLAN = {
  /** Chat en línea */
  CHAT_LINEA: 'CH',
  /** Domicilio */
  DOMICILIO: 'D',
  /** Correo electrónico */
  MAIL: 'MA',
  /** Telefónico */
  TELEFONICO: 'TL',
  /** Videochat */
  VIDEOCHAT: 'VC',
  /** Videollamada */
  VIDEOLLAMADA: 'VL',
  /** Skype Empresarial */
  SKYPE_EMPRESARIAL: 'SK',
  /** WhatsApp Entrante */
  WHATSAPP_ENTRADA: 'WE',
  /** Agente Virtual */
  AGENTE_VIRTUAL: 'AV'
} as const;

// ============================================================================
// PERIODICIDAD DE CITAS - Legacy: Constants.java (D, W, M, Y)
// ============================================================================

export const PERIODICIDAD = {
  /** Diario */
  DIARIO: 'D',
  /** Semanal */
  SEMANAL: 'S',
  /** Mensual */
  MENSUAL: 'M',
  /** Anual */
  ANUAL: 'A'
} as const;

// ============================================================================
// PATRONES Y FORMATOS - Legacy: ConstantsEJB
// ============================================================================

/** Patrón para validar emails */
export const EMAIL_PATTERN = /^[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9-]{1,63}(?:\.[A-Za-z0-9-]{1,63}){1,8}$/;

/** Formato de fecha: yyyyMMdd */
export const PATTERN_YYYYMMDD = 'yyyyMMdd';

/** Formato de fecha: yyyy/MM/dd */
export const PATTERN_YYYY_MM_DD = 'yyyy/MM/dd';

/** Formato de fecha y hora */
export const PATTERN_DD_MM_YYYY_HH_MM_SS = 'dd/MM/yyyy HH:mm:ss';

// ============================================================================
// RESPUESTAS DE LLAMADAS - Legacy: ConstantsEJB.TpscRespuestaLlamadas
// ============================================================================

export const RESPUESTA_LLAMADA = {
  /** No contestan */
  NO_CONTESTAN: 1,
  /** Teléfono ocupado */
  TELEFONO_OCUPADO: 3,
  /** Contacto no se encuentra */
  CONTACTO_NO_ENCONTRADO: 4,
  /** Teléfono errado */
  TELEFONO_ERRADO: 13,
  /** No es contacto de la orden */
  NO_ES_CONTACTO_ORDEN: 14,
  /** Contacto no responde llamadas ni correos */
  CONTACTO_NO_RESPONDE: 15
} as const;

// ============================================================================
// CANALES DE ATENCIÓN - Legacy: ConstantsEJB.TiposCanales
// ============================================================================

export const CANAL = {
  /** Canal contact center */
  CONTACT: 'Canal contact',
  /** Canal consultor */
  CONSULTOR: 'Canal consultor'
} as const;

// ============================================================================
// CATÁLOGOS SIN ORIENTACIÓN DE SERVICIO - Reutilizables UI
// ============================================================================

/**
 * Recurso SEUS que decide qué lista de formas de entrega se muestra.
 * Legacy: UtilidadesBuscadores.llenarListBoxFormaEntrega() invoca
 * Utilidades.hasAccess("rsSelModoEntrega", accessCb, denyCb).
 */
export const RECURSO_MODO_ENTREGA = 'rsSelModoEntrega';

/**
 * Recurso SEUS nuevo que controla el acceso a la pantalla de Programación Masiva.
 * Se carga en SEUS con los perfiles equivalentes del recurso legacy ProgramacionMasivaPlace.
 */
export const RECURSO_PROGRAMACION_MASIVA_AGENDA =
  'agenda/programacion-masiva';

/**
 * Recurso SEUS que habilita, en "Administrar agenda", el buscador para ver la agenda
 * de otros asesores. Sin el recurso, el usuario solo ve su propia agenda.
 */
export const RECURSO_ADMINISTRAR_OTRAS_AGENDAS = 'administrarOtrasAgendas';

/**
 * Recursos SEUS de acciones masivas en Sin Orientacion de Servicios.
 * Legacy: CumplimientoSinOcViewImpl.ui.xml, anchors con seguro="true".
 */
export const RECURSO_CUMPLIMIENTO_SIN_OC_MASIVO = 'rsCumplimientoSinOCMasivo';
export const RECURSO_GESTIONES_MASIVAS_SIN_OC = 'rsMasivaGestiones';

/**
 * Recurso SEUS que decide quién ve y ejecuta el botón "Crear Tarea".
 * Legacy: CumplimientoSinOcViewImpl.ui.xml, siCrearTarea (seguro="true").
 */
export const RECURSO_CREAR_TAREA_SIN_OC = 'siCrearTarea';

/**
 * Formas de entrega para asesores línea de atención (CON acceso al recurso
 * `rsSelModoEntrega`).
 * Legacy: UtilidadesBuscadores.llenarListBoxFormaEntrega() — rama accessCb.
 */
export const FORMAS_ENTREGA_LINEA_ATENCION: Array<{ label: string; value: string }> = [
  { label: 'CHAT EN LINEA',    value: TIPO_PLAN.CHAT_LINEA },
  { label: 'MAIL',             value: TIPO_PLAN.MAIL },
  { label: 'TELEFONICO',       value: TIPO_PLAN.TELEFONICO },
  { label: 'VIDEOLLAMADA',     value: TIPO_PLAN.VIDEOLLAMADA },
  { label: 'WHATSAPP ENTRADA', value: TIPO_PLAN.WHATSAPP_ENTRADA },
];

/**
 * Formas de entrega para asesores de campo (SIN acceso al recurso
 * `rsSelModoEntrega`).
 * Legacy: UtilidadesBuscadores.llenarListBoxFormaEntrega() — rama denyCb.
 */
export const FORMAS_ENTREGA_ASESOR_CAMPO: Array<{ label: string; value: string }> = [
  { label: 'DOMICILIO',        value: TIPO_PLAN.DOMICILIO },
  { label: 'SKYPE EMPRESARIAL',value: TIPO_PLAN.SKYPE_EMPRESARIAL },
];

/**
 * Opciones de tipo de llamada para formularios Sin OC.
 */
export const TIPOS_LLAMADA_SIN_OC = [
  { label: 'LLAMADA ENTRANTE', value: 'LLAMADA ENTRANTE' },
  { label: 'LLAMADA SALIENTE', value: 'LLAMADA SALIENTE' },
];

/**
 * Opciones de canal de servicio para formularios Sin OC.
 */
export const CANALES_SERVICIO_SIN_OC = [
  { label: 'Canal consultor', value: CANAL.CONSULTOR },
  { label: 'Canal contact', value: CANAL.CONTACT },
];

/**
 * Opciones de estado de investigación para formularios Sin OC.
 */
export const ESTADOS_INVESTIGACION_SIN_OC = [
  { label: 'En proceso', value: 'En proceso' },
  { label: 'Terminada', value: 'Terminada' },
];

/**
 * Justificaciones de reprogramación usadas en formularios de cumplimiento.
 */
export const JUSTIFICACIONES_REPROGRAMACION = [
  { label: 'Reprogramación por cliente', value: 1 },
  { label: 'Reprogramación por consultor', value: 2 },
  { label: 'Incapacidad del consultor', value: 3 },
  { label: 'Fuerza mayor', value: 4 },
  { label: 'Otro', value: 99 },
];

// ============================================================================
// TIPOS DE PROCESOS - Legacy: ConstantsEJB.PROCESO_*
// ============================================================================

export const PROCESO = {
  /** Proceso manual */
  MANUAL: 'MANUAL',
  /** Proceso automático */
  AUTOMATICO: 'AUTOMATICO'
} as const;

// ============================================================================
// CATALOGOS DE CAMPOS DINAMICOS - Migrado de Constants.java
// ============================================================================

/**
 * Grupos de Estudio (1-30)
 * Legacy: Utilidades.setListBoxValues(slbNumeroGrupoEstudio, Constants.LIST_GRUPO_ESTUDIO)
 */
export const CATALOGO_GRUPOS_ESTUDIO = Array.from({ length: 30 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1).padStart(2, '0')
}));

/**
 * Tipos de Formación Técnica
 * Legacy: Constants.FORMACION_VIVIR, FORMACION_SINCRONICA, FORMACION_EVENTO_EXTERNO, FORMACION_INDUCCION
 */
export const CATALOGO_TIPOS_FORMACION = [
  { value: 'Vivir el aprendizaje', label: 'Vivir el aprendizaje' },
  { value: 'Formación sincrónica (presencial/virtual)', label: 'Formación sincrónica (presencial/virtual)' },
  { value: 'Evento externo', label: 'Evento externo' },
  { value: 'Inducción y entrenamiento coorporativo', label: 'Inducción y entrenamiento corporativo' } // value mantiene typo original de legacy
] as const;

/**
 * Tipos de Investigación Aplicada
 * Legacy: Constants.INVESTIGACION_PROYECTO, INVESTIGACION_EVENTO, INVESTIGACION_ARTICULO, INVESTIGACION_DISENO
 */
export const CATALOGO_TIPOS_INVESTIGACION = [
  { value: 'Proyecto de investigación', label: 'Proyecto de investigación' },
  { value: 'Evento especializado', label: 'Evento especializado' },
  { value: 'Articulo cientifico', label: 'Artículo científico' }, // value mantiene formato original de legacy
  { value: 'Diseño/patente O producto', label: 'Diseño/patente o producto' }
] as const;

// ============================================================================
// SERVICIOS SAP (HU1287272) — Administración › Maestro › Servicios SAP
// ============================================================================

/**
 * Longitud máxima del código de servicio SAP (solo dígitos).
 * Legacy: tbCodigo maxLength="8" en AdminServiciosSapViewImpl.ui.xml.
 */
export const SERVICIO_SAP_CODIGO_MAX_LENGTH = 8;

/**
 * Longitud máxima de la descripción del servicio SAP.
 * Legacy: tbNombre maxLength="50" en RegistroServiciosSapViewImpl.ui.xml.
 */
export const SERVICIO_SAP_DESCRIPCION_MAX_LENGTH = 50;

/**
 * Caracteres permitidos en la descripción del servicio SAP: letras, números, espacio y
 * `. , ; : ( ) / _ ' " # % + - ! ? ¿ ¡`. Es la misma lista blanca (FREE_TEXT) que valida el servidor,
 * para avisar en pantalla antes de que el backend rechace la solicitud con un 400.
 */
export const SERVICIO_SAP_DESCRIPCION_REGEX =
  /^[\p{L}\p{N} .,;:()/_'"#%+\-!?¿¡]*$/u;

// ============================================================================
// METAS MASIVAS (HU1290096) — Administración › Maestros › Metas
// ============================================================================

/**
 * Expresión del Año de la meta (solo dígitos).
 * Legacy: Constants.REGEX_JUST_NUMBERS = "[0-9]{1,}".
 */
export const META_MASIVA_ANIO_REGEX = /^[0-9]{1,}$/;

/**
 * Longitud máxima del Año de la meta.
 * Legacy: tbAnio / tbAno con maxLength 4 (ListarMetasAnioViewImpl, NuevaMetaMasivaViewImpl).
 */
export const META_MASIVA_ANIO_MAX_LENGTH = 4;

/**
 * Formato de la Meta Mes: hasta 3 enteros y 2 decimales.
 * Legacy: Constants.REGEX_PERCENT_NUMBERS = "^(\d{0,3}(\.\d{0,2}){0,1})$".
 */
export const META_MASIVA_META_MES_REGEX = /^(\d{0,3}(\.\d{0,2}){0,1})$/;

/**
 * Longitud máxima de la Meta Mes (tabla y modal).
 * Legacy: setMaxLength(6) en la columna editable y en tbMeta.
 */
export const META_MASIVA_META_MES_MAX_LENGTH = 6;

/** Registros por página del listado de metas (el legacy muestra 10; no es configurable). */
export const META_MASIVA_PAGE_SIZE = DEFAULT_ROWS_PER_PAGE;

/** Código del mes "TODOS" en el modal de Nueva Meta Masiva (se procesan los meses 1..12). */
export const META_MASIVA_MES_TODOS = 0;

/**
 * Meses para el combo del modal y para mostrar el mes en letras en la tabla.
 * Legacy: UtilidadesBuscadores.llenarListBoxMeses(..., opcionTodos = true) / listDatosConsultaMeses.
 */
export const MESES_META: readonly { codigo: number; descripcion: string }[] = [
  { codigo: META_MASIVA_MES_TODOS, descripcion: 'TODOS' },
  { codigo: 1, descripcion: 'ENERO' },
  { codigo: 2, descripcion: 'FEBRERO' },
  { codigo: 3, descripcion: 'MARZO' },
  { codigo: 4, descripcion: 'ABRIL' },
  { codigo: 5, descripcion: 'MAYO' },
  { codigo: 6, descripcion: 'JUNIO' },
  { codigo: 7, descripcion: 'JULIO' },
  { codigo: 8, descripcion: 'AGOSTO' },
  { codigo: 9, descripcion: 'SEPTIEMBRE' },
  { codigo: 10, descripcion: 'OCTUBRE' },
  { codigo: 11, descripcion: 'NOVIEMBRE' },
  { codigo: 12, descripcion: 'DICIEMBRE' }
];

/** Texto de la opción vacía de los combos. Legacy: Utilidades.getConstants().selectOneOption(). */
export const PLACEHOLDER_SELECCIONE = '--Seleccione una opción--';

/**
 * Mensaje genérico de falla técnica, sin detalle del error.
 * Legacy: WidgetsNamesConstants.genericError().
 */
export const MENSAJE_FALLA_TECNICA =
  'Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.';
